import { Dispatch, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

//
import { ExtraHeaderContent, Header } from 'adminDashboard/benefitDetails/common/ProductClassSummaryComponents'

import { EndDatePerkTypeRequest, Feature, getBenefitDetails, setPerkEndDate } from 'core'
//
import getProductClassDesignSummary, {
  ProductClassDesignSummaryResponse,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getProductClassDesignSummary'
import { BenefitProductClassResponse } from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'

import useFeatureFlag from 'common/hooks/useFeatureFlag'

//
import { Button, Card, Col, Collapse, CollapseItem, Icon, Notification, Row, Skeleton } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import ActiveAndPastPerksHistoryTabs from './activeAndPastPerksHistoryTabs'
import { AddNewPerkModal } from './addNewPerk/AddNewPerkModal'
import EndPerkModal from './endPerk/endPerkModal'

import './product-class-details.less'

export type ProductClassProps = {
  item: BenefitProductClassResponse
  collapseKey: number
  colSpan?: number
  planPeriodId: string
  displayActionColumn?: boolean
  onEdit?: () => any
  handleBenefitDesign?: () => any
  update?: boolean
  setUpdate?: Dispatch<boolean>
  onDelete?: () => any
}

const PerksProductClassDetails = ({
  item,
  collapseKey,
  colSpan = 12,
  planPeriodId,
  displayActionColumn,
  onEdit,
  update = false,
  setUpdate = () => undefined,
  onDelete,
}: ProductClassProps) => {
  const { t: translate } = useTranslation()
  const [rowsToDisable, setRowsToDisable] = useState([])
  const { data: activePlanPeriod } = useQueryRequestV2({
    queryFn: () => {
      return getBenefitDetails(planPeriodId)
    },
  })

  const {
    data: productClasses,
    isLoading: areProductClassesLoading,
    reload: reloadProductClassDetails,
  } = useQueryRequestV2<ProductClassDesignSummaryResponse>({
    queryFn: () => getProductClassDesignSummary(item.id),
    initialState: { eligibleExpenseCategories: [], waitingPeriod: 0, totalAllowance: ' ', offerSameAllowance: false },
  })
  const [showEndPerkModal, setShowEndPerkModal] = useState<boolean>(false)
  const [showAddPerkModal, setAddNewPerkModal] = useState<boolean>(false)
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
  const [loadingBenefitDetails, setLoadingbenefitDetails] = useState<boolean>(false)
  const [endDatePerk, setEndDatePerk] = useState<string>(null)
  const [runOutDays, setRunOutDays] = useState<number>(30)
  const [expenseCategory, setExpenseCategory] = useState<number>(0)
  const [productClassId, setProductClassId] = useState<string>(null)

  const { sendCommand: sendPerkEndDate } = useCommandRequestV2((request: EndDatePerkTypeRequest) =>
    setPerkEndDate(request),
  )
  const canAddNewPerk = useFeatureFlag(Feature.AddNewPerk)
  /**
   * update is a flag sent by parent to trigger details update from back-end,
   * the component after update finished sets the flag again to false
   */
  useEffect(() => {
    if (update) {
      reloadProductClassDetails().finally(() => {
        setUpdate(false)
      })
    }
  }, [update])

  useEffect(() => {
    let productClassCategories = productClasses.eligibleExpenseCategories.map((el) => {
      return { name: el.expenseCategoryId, isDisable: false }
    })
    setRowsToDisable(productClassCategories)
  }, [productClasses])

  const refreshTableData = () => {
    setAddNewPerkModal(false)
    reloadProductClassDetails()
  }

  const openEndPerkModal = (expenseCategoryId: number, date: string, productClassId: string) => {
    setExpenseCategory(expenseCategoryId)
    setShowEndPerkModal(true)
    setEndDatePerk(date)
    setProductClassId(productClassId)
  }

  const openDeletePerkModal = (expenseCategoryId: number) => {
    setExpenseCategory(expenseCategoryId)
  }
  const submitPerkEndDate = async () => {
    setLoadingSubmit(true)
    const request: EndDatePerkTypeRequest = {
      finalEndDate: endDatePerk,
      runOutDays: runOutDays,
      expenseCategoryId: expenseCategory,
      productClassId: productClassId,
    }
    const result = await sendPerkEndDate(request)
    if (result.errors.length <= 0) {
      Notification.open({
        message: 'Successful',
        type: 'success',
      })
      setShowEndPerkModal(false)
      rowsToDisable[expenseCategory] = true
      setLoadingbenefitDetails(true)
      await reloadProductClassDetails()
      setLoadingbenefitDetails(false)
    } else {
      Notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
    setLoadingSubmit(false)
  }

  return item.designCompleted ? (
    <Row className={'mb-8'}>
      <Col span={colSpan} className="pk-perks__design-classes">
        <Row>
          <Col span={24}>
            <Row>
              <Col span={24}>
                <Collapse className="design_classes_collapse w-100">
                  <CollapseItem
                    className="class_design"
                    key={collapseKey}
                    header={
                      <div>
                        <Header title={item.name} />
                      </div>
                    }
                    extra={
                      <ExtraHeaderContent
                        content={
                          <div>
                            {onEdit && item.totalEmployees === 0 && (
                              <Button className="mr-16" onClick={onEdit}>
                                {translate('admin-dashboard.benefit-details.summary.edit-button')}
                              </Button>
                            )}

                            {onDelete && item.totalEmployees === 0 && (
                              <Icon className="fal fa-trash-alt" onClick={onDelete} />
                            )}
                          </div>
                        }
                      />
                    }
                    content={
                      <Skeleton loading={areProductClassesLoading}>
                        {productClasses && activePlanPeriod && (
                          <div>
                            {productClasses?.eligibleExpenseCategories?.length > 0 && (
                              <Card>
                                <Row className={'flex flex__space_between flex__align_center mb-16'}>
                                  <div>
                                    <h5>Class name </h5>
                                    {item.name}
                                  </div>
                                  <div>
                                    <h5>Waiting period </h5>
                                    {productClasses.waitingPeriod !== 0
                                      ? `${productClasses.waitingPeriod} ${translate(
                                          'admin-dashboard.benefit-details.summary.waiting-period-value-label',
                                        )}`
                                      : translate(
                                          'admin-dashboard.benefit-details.summary.no-waiting-period-value-label',
                                        )}
                                  </div>
                                  <div>
                                    {!displayActionColumn && canAddNewPerk && activePlanPeriod.isContinuous && (
                                      <Button icon="far fa-plus" onClick={() => setAddNewPerkModal(true)}>
                                        Add New Perk
                                      </Button>
                                    )}
                                  </div>
                                </Row>

                                <ActiveAndPastPerksHistoryTabs
                                  eligibleExpenseCategories={productClasses?.eligibleExpenseCategories}
                                  offerSameAllowance={item?.offerSameAllowance}
                                  loadingBenefitDetails={loadingBenefitDetails}
                                  activePlanPeriod={activePlanPeriod}
                                  displayActionColumn={displayActionColumn}
                                  onOpenEndPerkModalClick={(expenseType, date) =>
                                    openEndPerkModal(expenseType, date, item.id)
                                  }
                                  onOpenDeleteFuturePerkModalClick={openDeletePerkModal}
                                  item={item}
                                  expenseCategoryId={expenseCategory}
                                  productClassId={item.id}
                                  onDeleteFuturePerk={reloadProductClassDetails}
                                />
                              </Card>
                            )}
                            {showEndPerkModal && activePlanPeriod.isContinuous && (
                              <EndPerkModal
                                showEndPerkModal={showEndPerkModal}
                                loadingSubmit={loadingSubmit}
                                endDatePerk={endDatePerk}
                                endPerkRunOutDays={runOutDays}
                                onCancel={() => setShowEndPerkModal(false)}
                                onPerkEndDateChange={(val) => setEndDatePerk(val)}
                                onRunOutDaysChange={(val) => setRunOutDays(val)}
                                onSubmit={submitPerkEndDate}
                              />
                            )}
                            {canAddNewPerk && showAddPerkModal && activePlanPeriod.isContinuous && (
                              <AddNewPerkModal
                                productClassId={item.id}
                                planPeriod={activePlanPeriod}
                                showAddNewPerkModal={showAddPerkModal}
                                offerSameAllowance={item.offerSameAllowance}
                                eligibleExpenseCategories={productClasses?.eligibleExpenseCategories}
                                onCancel={() => {
                                  setAddNewPerkModal(false)
                                }}
                                onSubmit={refreshTableData}
                              />
                            )}
                          </div>
                        )}
                      </Skeleton>
                    }
                  />
                </Collapse>
              </Col>
            </Row>
            {/*</Card>*/}
          </Col>
        </Row>
      </Col>
    </Row>
  ) : (
    <Row className={'mb-8'}>
      <Col span={12}>
        <Card key={item.id}>
          <Row>
            <Col span={22}>
              <h5>{item.name}</h5>
            </Col>
            <Col span={2} className={'text-right'}>
              <Icon className="fal fa-trash-alt" onClick={onDelete} />
            </Col>
          </Row>
          <Button type={'primary'} ghost onClick={onEdit}>
            {translate('admin-dashboard.benefit-details.perks-benefit-design.button-title')}
          </Button>
        </Card>
      </Col>
    </Row>
  )
}

export default PerksProductClassDetails
